<script setup>
import { ref, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert, useTrack } from 'dashboard/composables';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import contactFilterItems from 'dashboard/routes/dashboard/contacts/contactFilterItems';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { useLocalizedLists } from 'shared/composables/useLocalizedLists';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';

import ContactsHeader from 'dashboard/components-next/Contacts/ContactsHeader/ContactHeader.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import ContactExportDialog from 'dashboard/components-next/Contacts/ContactsForm/ContactExportDialog.vue';
import ContactImportDialog from 'dashboard/components-next/Contacts/ContactsForm/ContactImportDialog.vue';
import CreateSegmentDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateSegmentDialog.vue';
import DeleteSegmentDialog from 'dashboard/components-next/Contacts/ContactsForm/DeleteSegmentDialog.vue';
import ContactsFilter from 'dashboard/components-next/filter/ContactsFilter.vue';
import AceleresNewConversationPanel from 'dashboard/components/AceleresNewConversationPanel.vue';
import AceleresStartConversationFlow from 'dashboard/components/AceleresStartConversationFlow.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { mobileContactOverlayClasses } from 'dashboard/constants/mobileFloatingActionButton';

const props = defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  segmentsId: { type: [String, Number], default: 0 },
  activeSegment: { type: Object, default: null },
  hasAppliedFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:sort',
  'search',
  'applyFilter',
  'clearFilters',
]);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const { isAdmin } = useAdmin();

const createNewContactDialogRef = ref(null);
const contactExportDialogRef = ref(null);
const contactImportDialogRef = ref(null);
const createSegmentDialogRef = ref(null);
const deleteSegmentDialogRef = ref(null);

const showFiltersModal = ref(false);
const appliedFilter = ref([]);
const segmentsQuery = ref({});

const appliedFilters = useMapGetter('contacts/getAppliedContactFiltersV4');
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const labels = useMapGetter('labels/getLabels');

// Aceleres: "Enviar Mensagem" reaproveita integralmente o fluxo do Chat de
// Atendimento — mesmo painel para informar telefone/localizar contato, mesmo
// modal de seleção de instância e mesmas regras de contato já em atendimento.
const inboxesList = useMapGetter('inboxes/getInboxes');
const showNewConversationPanel = ref(false);
const startConversationFlowRef = ref(null);
const accountId = computed(() => route.params.accountId);

// Sem BUS_EVENTS.NEW_CONVERSATION_MODAL aqui: o único ouvinte é o ReplyBox, que
// só existe na rota de conversas.
const openNewConversationPanel = () => {
  if (!inboxesList.value?.length) {
    store.dispatch('inboxes/get');
  }
  showNewConversationPanel.value = true;
};

const closeNewConversationPanel = () => {
  showNewConversationPanel.value = false;
};

const openContactPage = contact => {
  router.push(`/app/accounts/${accountId.value}/contacts/${contact.id}`);
};

const startConversationFromPanel = phone => {
  closeNewConversationPanel();
  startConversationFlowRef.value?.startForPhone(phone);
};

const openContactChatFromPanel = contact => {
  closeNewConversationPanel();
  if (contact?.phone_number) {
    startConversationFlowRef.value?.startForPhone(contact.phone_number);
    return;
  }
  openContactPage(contact);
};

const editContactFromPanel = contact => {
  closeNewConversationPanel();
  openContactPage(contact);
};
const hasActiveSegments = computed(
  () => props.activeSegment && props.segmentsId !== 0
);
const activeSegmentName = computed(() => props.activeSegment?.name);

const openCreateNewContactDialog = () => {
  createNewContactDialogRef.value?.dialogRef.open();
};
const openContactImportDialog = () =>
  contactImportDialogRef.value?.dialogRef.open();
const openContactExportDialog = () =>
  contactExportDialogRef.value?.dialogRef.open();
const openCreateSegmentDialog = () =>
  createSegmentDialogRef.value?.dialogRef.open();
const openDeleteSegmentDialog = () =>
  deleteSegmentDialogRef.value?.dialogRef.open();

const onCreate = async contact => {
  try {
    await store.dispatch('contacts/create', contact);
    createNewContactDialogRef.value?.onSuccess();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
      if (error.contactId) {
        await router.push({
          name: 'contacts_edit',
          params: {
            accountId: route.params.accountId,
            contactId: error.contactId,
          },
        });
      }
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onImport = async file => {
  try {
    await store.dispatch('contacts/import', file);
    contactImportDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SUCCESS_MESSAGE')
    );
    useTrack(CONTACTS_EVENTS.IMPORT_SUCCESS);
  } catch (error) {
    useAlert(
      error.message ??
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.ERROR_MESSAGE')
    );
    useTrack(CONTACTS_EVENTS.IMPORT_FAILURE);
  }
};

const onExport = async query => {
  try {
    await store.dispatch('contacts/export', query);
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error.message ||
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.ERROR_MESSAGE')
    );
  }
};

const onCreateSegment = async payload => {
  try {
    const payloadData = {
      ...payload,
      query: segmentsQuery.value,
    };
    const response = await store.dispatch('customViews/create', payloadData);
    createSegmentDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.SUCCESS_MESSAGE')
    );
    const segmentId = response?.data?.id;
    if (!segmentId) return;
    // Navigate to the created segment
    router.push({
      name: 'contacts_dashboard_segments_index',
      params: { segmentId },
      query: { page: 1 },
    });
  } catch {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.ERROR_MESSAGE')
    );
  }
};

const onDeleteSegment = async payload => {
  try {
    await store.dispatch('customViews/delete', {
      id: Number(props.segmentsId),
      ...payload,
    });
    router.push({
      name: 'contacts_dashboard_index',
      query: {
        page: 1,
      },
    });
    deleteSegmentDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.ERROR_MESSAGE')
    );
  }
};

const closeAdvanceFiltersModal = () => {
  showFiltersModal.value = false;
  appliedFilter.value = [];
};

const clearFilters = async () => {
  emit('clearFilters');
};

const onApplyFilter = async payload => {
  payload = useSnakeCase(payload);
  segmentsQuery.value = filterQueryGenerator(payload);
  emit('applyFilter', filterQueryGenerator(payload));
  showFiltersModal.value = false;
};

const onUpdateSegment = async (payload, segmentName) => {
  payload = useSnakeCase(payload);
  const payloadData = {
    ...props.activeSegment,
    name: segmentName,
    query: filterQueryGenerator(payload),
  };
  await store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
};

const { localizedCountries } = useLocalizedLists();

const setParamsForEditSegmentModal = () => {
  return {
    countries: localizedCountries.value,
    filterTypes: contactFilterItems,
    allCustomAttributes: useSnakeCase(contactAttributes.value),
    labels: labels.value || [],
  };
};

const initializeSegmentToFilterModal = segment => {
  const query = unref(segment)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditSegmentModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
};

const onToggleFilters = () => {
  if (showFiltersModal.value) {
    closeAdvanceFiltersModal();
    return;
  }

  appliedFilter.value = [];
  if (hasActiveSegments.value) {
    initializeSegmentToFilterModal(props.activeSegment);
  } else {
    appliedFilter.value = props.hasAppliedFilters
      ? [...appliedFilters.value]
      : [
          {
            attributeKey: 'name',
            filterOperator: 'equal_to',
            values: '',
            queryOperator: 'and',
            attributeModel: 'standard',
          },
        ];
  }
  showFiltersModal.value = true;
};

defineExpose({
  onToggleFilters,
});
</script>

<template>
  <ContactsHeader
    :show-search="showSearch"
    :search-value="searchValue"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :header-title="headerTitle"
    :is-segments-view="hasActiveSegments"
    :is-label-view="isLabelView"
    :is-active-view="isActiveView"
    :has-active-filters="hasAppliedFilters"
    :can-manage-directory="isAdmin"
    can-create-contact
    :button-label="t('CONTACTS_LAYOUT.HEADER.MESSAGE_BUTTON')"
    @search="emit('search', $event)"
    @update:sort="emit('update:sort', $event)"
    @add="openCreateNewContactDialog"
    @import="openContactImportDialog"
    @export="openContactExportDialog"
    @filter="onToggleFilters"
    @create-segment="openCreateSegmentDialog"
    @delete-segment="openDeleteSegmentDialog"
    @new-conversation="openNewConversationPanel"
  >
    <template #filter>
      <div
        class="absolute top-full z-40 mt-1 w-[calc(100vw-1.5rem)] overflow-visible ltr:-right-52 rtl:-left-52 sm:inset-x-auto sm:w-auto sm:ltr:right-0 sm:rtl:left-0"
        :class="mobileContactOverlayClasses"
      >
        <ContactsFilter
          v-if="showFiltersModal"
          v-model="appliedFilter"
          :segment-name="activeSegmentName"
          :is-segment-view="hasActiveSegments"
          @apply-filter="onApplyFilter"
          @update-segment="onUpdateSegment"
          @close="closeAdvanceFiltersModal"
          @clear-filters="clearFilters"
        />
      </div>
    </template>
  </ContactsHeader>

  <!-- Palco do painel "Nova conversa". Na página de Contatos ele é apresentado
       como modal centralizado, e não como a faixa lateral do Chat de
       Atendimento: aqui não existe coluna de conversas para servir de moldura.
       O painel em si é o mesmo componente, com as mesmas regras. -->
  <div
    class="acl-contacts-newconv-stage"
    :class="{ 'acl-contacts-newconv-stage--open': showNewConversationPanel }"
  >
    <div class="acl-contacts-newconv-modal">
      <AceleresNewConversationPanel
        :show="showNewConversationPanel"
        :account-id="accountId"
        panel-variant="contacts-modal"
        :show-dialer="false"
        @close="closeNewConversationPanel"
        @start-phone-conversation="startConversationFromPanel"
        @open-contact-chat="openContactChatFromPanel"
        @open-existing-phone="startConversationFromPanel"
        @edit-contact="editContactFromPanel"
      />
    </div>
  </div>
  <AceleresStartConversationFlow
    ref="startConversationFlowRef"
    :account-id="accountId"
  />

  <CreateNewContactDialog ref="createNewContactDialogRef" @create="onCreate" />
  <ContactExportDialog
    v-if="isAdmin"
    ref="contactExportDialogRef"
    @export="onExport"
  />
  <ContactImportDialog
    v-if="isAdmin"
    ref="contactImportDialogRef"
    @import="onImport"
  />
  <CreateSegmentDialog ref="createSegmentDialogRef" @create="onCreateSegment" />
  <DeleteSegmentDialog ref="deleteSegmentDialogRef" @delete="onDeleteSegment" />
</template>

<style>
/* Sem `scoped`: o drawer é renderizado dentro do componente filho e não recebe
   o atributo de escopo deste componente. */
.acl-contacts-newconv-stage {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  /* Fechado, o palco não pode capturar cliques da lista de contatos atrás.
     Aberto, o clique no escurecido atravessa até o backdrop do próprio painel,
     que é quem fecha. */
  pointer-events: none;
}

.acl-contacts-newconv-stage--open {
  background: rgba(15, 23, 42, 0.45);
}

/* O drawer do painel é `absolute inset-0`, então esta caixa é a moldura que
   define o tamanho do modal. */
.acl-contacts-newconv-modal {
  position: relative;
  width: min(460px, 92vw);
  height: min(620px, 82vh);
  overflow: hidden;
  border-radius: 16px;
}

.acl-contacts-newconv-stage--open .acl-contacts-newconv-modal {
  pointer-events: auto;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.28);
}
</style>
